import {Trans} from '@lingui/macro';
import React, {FC, ReactNode, useState} from 'react';
import styled, {useTheme} from 'styled-components';

import {ChromelessButton} from '../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {SettingsFieldLine, SettingsFieldLineProps} from './settingsFieldLine';

/*
 * Props.
 */

interface SettingsAccordionFieldLineProps
  extends Omit<SettingsFieldLineProps, 'content' | 'hasVerticalLayout'> {
  initialIsOpen?: boolean;
  summaryValue?: ReactNode;
}

/*
 * Style.
 */

const StyledContentToggle = styled(ChromelessButton)`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  grid-gap: 10px;
  color: ${(p) => p.theme.greys.shade60};
`;

const StyledIcon = styled(Icon)<{$isOpen: boolean}>`
  transform: ${(p) => (p.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

/*
 * Component.
 */

export const SettingsAccordionFieldLine: FC<SettingsAccordionFieldLineProps> = (props) => {
  const {children, summaryValue, ...fieldLineProps} = props;

  const [isOpen, setIsOpen] = useState(props.initialIsOpen ?? false);
  const theme = useTheme();

  return (
    <>
      <SettingsFieldLine
        {...fieldLineProps}
        hasVerticalLayout={false}
        content={
          <StyledContentToggle onClick={() => setIsOpen((v) => !v)}>
            {summaryValue}
            <StyledIcon name="chevronExpandSettings" color={theme.greys.shade80} $isOpen={isOpen} />
          </StyledContentToggle>
        }
      />
      {isOpen && children}
    </>
  );
};

export function renderSummaryValueForBoolean(value: boolean) {
  if (value) {
    return <Trans id="summaryvalue-enabled">Enabled</Trans>;
  }

  return <Trans id="summaryvalue-disabled">Disabled</Trans>;
}
